import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import styles from './index.module.css';

// grab a leading YYYY-MM-DD from any date-ish string (e.g. "2025-11-10 00:00:00")
const ymdFromAnything = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};

// normalize value/min/max/default to a valid dayjs or null
const coerceDayjs = (v) => {
  if (!v) return null;
  if (dayjs.isDayjs(v)) return v.isValid() ? v : null;
  if (v instanceof Date) {
    const d = dayjs(v);
    return d.isValid() ? d : null;
  }
  // strings: prefer YYYY-MM-DD (drift-proof), else let dayjs try
  const ymd = ymdFromAnything(v);
  if (ymd) {
    const d = dayjs(ymd);
    return d.isValid() ? d : null;
  }
  const d = dayjs(String(v));
  return d.isValid() ? d : null;
};

const CustomDatePicker = ({
  label,
  labelAdornment,
  name,
  value,
  onChange,
  defaultValue,
  minValue,
  maxDate,
  isRequired,
  disabled = false,
  error = false,
  helperText = "",
}) => {
  const valueDj   = coerceDayjs(value);
  const minDateDj = coerceDayjs(minValue);
  const maxDateDj = coerceDayjs(maxDate);
  const defaultDj = coerceDayjs(defaultValue);

  return (
    <>
      <Typography
        sx={{ fontSize: '0.9rem', color: '#555', marginBottom: '0' }}
        variant="subtitle1"
        gutterBottom
      >
        {label} <span className={styles.requiredSymbol}>{isRequired ? '*' : ''}</span>
        {labelAdornment}
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          disabled={disabled}
          minDate={minDateDj || undefined}
          maxDate={maxDateDj || undefined}
          defaultValue={defaultDj || undefined}
          value={valueDj}
          // call with BOTH (newValue, name) so existing code keeps working
          onChange={(newVal) => {
            if (disabled) return;
            onChange?.(coerceDayjs(newVal), name);
          }}
          sx={{
            '& .MuiFormControl-root': { backgroundColor: '#fff' },
            '& .MuiInputBase-root': {
              border: `1px solid ${error ? '#d32f2f' : '#ccc'}`,
              borderRadius: '4px',
              backgroundColor: '#fff',
              padding: '8px',
              height: '40px',
              overflow: 'hidden'
            },
            '& .MuiInputBase-input': { padding: '0', border: 'none' },
            '& .MuiSvgIcon-root': { color: '#000' }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              disabled={disabled}
              inputProps={{ ...params.inputProps, readOnly: true }}
              // keep your existing display format (2-digit year) to avoid visual diffs
              value={valueDj ? valueDj.format('YY-MM-DD') : ''}
            />
          )}
        />
      </LocalizationProvider>
      {helperText ? (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: error ? '#d32f2f' : 'text.secondary' }}>
          {helperText}
        </Typography>
      ) : null}
    </>
  );
};

export default CustomDatePicker;
